import Row from 'components/Common/Row';
import NavSection from 'kingpin/navigation/NavSection';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import Button from 'kingpin/atoms/Button';
import NavItem from 'kingpin/navigation/NavItem';
import {
  V3AppsContext,
  V3SelectedAppContext,
} from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import usePopup from 'hooks/usePopup';
import InlineDialog from 'components/InlineDialog';
import { List, ListItem } from 'components/Inputs/Dropdown';
import Typography from 'kingpin/atoms/Typography';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import AddTabWizard from 'screens/EntityDetailsShow/TopBar/AddTabButton/AddTabWizard';
import { useEntityDetailsManageTabsLogic } from 'screens/EntityDetailsShow/TopBar/ManageTabsButton';
import TabLabelEditorModal from 'screens/EntityDetailsShow/TopBar/TabLabelEditorModal';
import Tabs from 'components/Tabs';
import useEntityAppAndTabs from '../../../Common/useEntityAppAndTabs';
import { ENTITY_APP_SIDEBAR_DROPDOWN_DIV_ID } from '../../../../../../constants';

const useBaseNavigationPath = () => {
  const { availableProductApps } = useContext(V3AppsContext);
  const { selectedProductApp, selectedProduct, selectedWorkspace } =
    useContext(V3SelectedAppContext);

  const baseNavigationPath = useMemo(() => {
    if (selectedWorkspace) {
      return `/workspaces/${selectedWorkspace.urlSlug}`;
    }

    if (!selectedProduct) {
      return undefined;
    }

    const subAppsThisUserHasAccessTo = availableProductApps.filter((app) =>
      selectedProduct.appIds.includes(app.id),
    );

    if (subAppsThisUserHasAccessTo.length === 1) {
      return `/apps/${selectedProduct.urlSlug}`;
    }

    if (selectedProductApp) {
      return `/apps/${selectedProduct.urlSlug}/${selectedProductApp.urlSlug}`;
    }

    return undefined;
  }, [
    availableProductApps,
    selectedProduct,
    selectedProductApp,
    selectedWorkspace,
  ]);

  return baseNavigationPath;
};

const EntityAppSideBarBackButton = ({
  entityNavItem,
}: {
  entityNavItem: V3.Navigation.EntityApp | V3.Navigation.MappedEntityView;
}) => {
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const navigate = useNavigate();
  const baseNavigationPath = useBaseNavigationPath();

  const onBackClicked = useCallback(() => {
    if (!selectedProductApp || !baseNavigationPath) {
      return;
    }

    const defaultItemKey =
      selectedProductApp.navigationSettings.defaultScreen.itemKey;
    const isDefault = defaultItemKey === entityNavItem.key;

    if (isDefault) {
      const allItems = selectedProductApp.navigationSettings.sections.reduce(
        (acc, s) => [...acc, ...s.items],
        [] as V3.Navigation.Item[],
      );
      const differentItem = allItems.find((i) => i.key !== defaultItemKey);
      if (differentItem) {
        navigate(`${baseNavigationPath}/${differentItem.key}`);
      } else {
        navigate('/');
      }
      return;
    }

    navigate(`${baseNavigationPath}/${defaultItemKey}`);
  }, [baseNavigationPath, entityNavItem.key, navigate, selectedProductApp]);

  if (!selectedProductApp) {
    return null;
  }

  return (
    <Button
      size={'Small'}
      type={'Secondary'}
      label={'Back'}
      icon={'chevron-left'}
      onClick={onBackClicked}
    />
  );
};

const ManageScreensPopup = ({
  tabs,
  app,
  close,
}: {
  app: EntityDetails.App;
  tabs: EntityDetails.ClientTab[];
  close: () => void;
}) => {
  const { fixedTabs, variableTabs, onOrderChanged, onTabRemoved, setTabLabel } =
    useEntityDetailsManageTabsLogic({ tabs, app });
  const [editingTab, setEditingTab] = useState<EntityDetails.ClientTab>();

  return (
    <>
      <Tabs.ManageTabs.Popup
        onOrderChanged={onOrderChanged}
        onTabRemoved={onTabRemoved}
        onTabEdit={setEditingTab}
        fixedTabs={fixedTabs}
        variableTabs={variableTabs}
        close={close}
      />
      <ModalTransition>
        {editingTab && (
          <TabLabelEditorModal
            tab={editingTab}
            onSave={(label) => setTabLabel({ tab: editingTab, label })}
            onClose={() => setEditingTab(undefined)}
          />
        )}
      </ModalTransition>
    </>
  );
};

const EntityAppSidebarEditButton = ({
  entityNavItem,
}: {
  entityNavItem: V3.Navigation.EntityApp | V3.Navigation.MappedEntityView;
}) => {
  const { isOpen, close, open } = usePopup();
  const {
    isOpen: isAddTabOpen,
    close: closeAddTab,
    open: openAddTab,
  } = usePopup();
  const {
    isOpen: isTabsManagerOpen,
    close: closeTabManager,
    open: openTabManager,
  } = usePopup();

  const { entityApp, tabs } = useEntityAppAndTabs(entityNavItem);

  if (!entityApp) {
    return null;
  }

  return (
    <>
      <InlineDialog
        content={
          <List>
            <ListItem
              onClick={() => {
                openAddTab();
                close();
              }}
            >
              <Typography.Body type={'Label'}>Add Screen</Typography.Body>
            </ListItem>
            <ListItem
              onClick={() => {
                openTabManager();
                close();
              }}
            >
              <Typography.Body type={'Label'}>Manage Screens</Typography.Body>
            </ListItem>
          </List>
        }
        isOpen={isOpen}
        onClose={close}
      >
        <Button
          type={'Ghost'}
          onClick={isOpen ? close : open}
          icon={'dots-menu'}
          size={'Medium'}
        />
      </InlineDialog>
      <AddTabPopup
        isOpen={isAddTabOpen}
        close={closeAddTab}
        entityApp={entityApp}
        entityNavItem={entityNavItem}
      />
      <ModalTransition>
        {isTabsManagerOpen && (
          <ManageScreensPopup
            close={closeTabManager}
            app={entityApp}
            tabs={tabs}
          />
        )}
      </ModalTransition>
    </>
  );
};

const AddTabPopup = ({
  isOpen,
  close,
  entityApp,
  entityNavItem,
}: {
  isOpen: boolean;
  close: () => void;
  entityApp: EntityDetails.App;
  entityNavItem: V3.Navigation.EntityApp | V3.Navigation.MappedEntityView;
}) => {
  const baseNavigationPath = useBaseNavigationPath();
  const { search } = useLocation();

  const buildTabLink = useCallback(
    ({
      tabId,
    }: {
      entityApp: EntityDetails.App;
      value: string;
      tabType: EntityDetails.TabType;
      tabId: string;
    }) => {
      return `${baseNavigationPath}/${entityNavItem.key}/${tabId}${search}`;
    },
    [baseNavigationPath, entityNavItem.key, search],
  );

  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
          <AddTabWizard
            close={close}
            app={entityApp}
            formTitle={'New Screen'}
            buildTabLink={buildTabLink}
          />
        </Modal>
      )}
    </ModalTransition>
  );
};

const EntityAppSidebarItem = ({
  tab,
  entityNavItem,
}: {
  tab: EntityDetails.ClientTab;
  entityNavItem: V3.Navigation.EntityApp | V3.Navigation.MappedEntityView;
}) => {
  const baseNavigationPath = useBaseNavigationPath();
  const { search } = useLocation();

  const to = useMemo(() => {
    return `${baseNavigationPath}/${entityNavItem.key}/${tab.typeId}${search}`;
  }, [baseNavigationPath, entityNavItem.key, search, tab.typeId]);

  const icon = useMemo((): Kingpin.Icon | undefined => {
    if (tab.type === 'dashboard') {
      return 'dashboard';
    } else if (tab.type === 'report') {
      return 'find-in-page';
    } else if (tab.type === 'board') {
      return 'navigation-goals';
    } else if (tab.type === 'driver-details-tab') {
      return 'navigation-metrics';
    } else if (tab.type === 'driver-bonus-program-report') {
      return 'navigation-metrics';
    } else if (tab.type === 'driver-bonus-combined') {
      return 'navigation-metrics';
    } else if (tab.type === 'scorecard') {
      return 'navigation-metrics';
    }

    return undefined;
  }, [tab.type]);

  return <NavItem label={tab.name} to={to} icon={icon} />;
};

const EntityAppSideBarItems = ({
  entityNavItem,
}: {
  entityNavItem: V3.Navigation.EntityApp | V3.Navigation.MappedEntityView;
}) => {
  const { entityApp, tabs } = useEntityAppAndTabs(entityNavItem);

  if (!entityApp) {
    return null;
  }

  return (
    <>
      {tabs.map((tab) => (
        <EntityAppSidebarItem
          key={tab.typeId}
          tab={tab}
          entityNavItem={entityNavItem}
        />
      ))}
    </>
  );
};

const EntityAppSideBar = ({
  entityNavItem,
}: {
  entityNavItem: V3.Navigation.EntityApp | V3.Navigation.MappedEntityView;
}) => {
  return (
    <>
      <Row
        centerAlign
        spaceBetween
        style={{
          padding: '16px 24px',
          borderBottom: `1px solid var(--color-grid-border)`,
        }}
      >
        <EntityAppSideBarBackButton entityNavItem={entityNavItem} />
        <EntityAppSidebarEditButton entityNavItem={entityNavItem} />
      </Row>
      <NavSection label={entityNavItem.label}>
        <div
          style={{ margin: '0px 8px 20px 8px' }}
          id={ENTITY_APP_SIDEBAR_DROPDOWN_DIV_ID}
        />
        <EntityAppSideBarItems entityNavItem={entityNavItem} />
      </NavSection>
    </>
  );
};

export default EntityAppSideBar;
